import { Button, FAIcon } from '@/components'
import { useConfirm } from '@/utils/hooks'
import { usePrompt } from '@ur-solutions/react-components'
import type { UseFormReturn } from '@ur-solutions/react-hooks'
import { useTranslate } from '@ur-solutions/react-hooks'
import uniqueId from 'lodash/uniqueId'
import type { PeriodAddonForm } from './PeriodAddonPrompt'
import { PeriodPrompt } from './PeriodAddonPrompt'
import { PeriodAddonView } from './PeriodAddonView'
import type { SalarySettingsForm } from './SalarySettingsFormView'

type PeriodField = 'periodAddons' | 'periodOvertimes'

export const PeriodList: React.FC<{
  form: UseFormReturn<SalarySettingsForm>
  field: PeriodField
}> = ({ form, field }) => {
  const translations = useTranslate({
    add: 'common.add',
    delete: 'common.delete-alt',
    deletePeriodAddon: 'settings.delete-period-addon',
    doYouWantToDeletePeriodAddon: 'settings.do-you-want-to-delete-period-addon',
  })
  const addPrompt = usePrompt()
  const addConfirm = useConfirm()
  const periods = form.formValues[field]

  const openPrompt = (period: PeriodAddonForm | null) =>
    addPrompt<PeriodAddonForm | null>(resolve => (
      <PeriodPrompt
        period={period}
        isAddon={field === 'periodAddons'}
        existingPeriods={periods}
        onSubmit={resolve}
      />
    ))

  async function addPeriod() {
    const { data } = await openPrompt(null)
    if (!data) return
    form.updateForm({
      [field]: [
        ...periods,
        {
          id: uniqueId('overtime-period-'),
          start: data.start,
          end: data.end,
          salaryCode: data.salaryCode,
          salaryCodeName: data.salaryCodeName,
          monday: data.monday,
          tuesday: data.tuesday,
          wednesday: data.wednesday,
          thursday: data.thursday,
          friday: data.friday,
          saturday: data.saturday,
          sunday: data.sunday,
          excludeIfOvertime: data.excludeIfOvertime,
          override: data.override,
          alwaysActive: data.alwaysActive ?? false,
        },
      ],
    })
  }

  async function editPeriod(period: PeriodAddonForm) {
    const { data } = await openPrompt(period)
    if (!data) return
    form.updateForm({
      [field]: periods.map(p => (p.id === period.id ? data : p)),
    })
  }

  async function deletePeriod(period: PeriodAddonForm) {
    const { data } = await addConfirm(
      translations.doYouWantToDeletePeriodAddon,
      translations.deletePeriodAddon,
      { confirmText: translations.delete }
    )
    if (!data) return
    form.updateForm({ [field]: periods.filter(p => p.id !== period.id) })
  }

  return (
    <div className="flex flex-col gap-2">
      {periods.map(period => (
        <PeriodAddonView
          key={period.id}
          periodAddon={period}
          onEdit={() => editPeriod(period)}
          onDelete={() => deletePeriod(period)}
        />
      ))}
      <Button
        className="self-start"
        variant="outline"
        size="lg"
        type="button"
        onClick={addPeriod}
      >
        <FAIcon icon="plus" />
        {translations.add}
      </Button>
    </div>
  )
}
